import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AxiosClient from '../../assets/AxiosClient';
import { useStateContext } from '../../Contexts/ContextProvider';

const LiveCountDisplay = () => {
  const { id } = useParams();
  const { SetNotification } = useStateContext();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchCount = async () => {
    try {
      const response = await AxiosClient.get(`/sessions/${id}`);
      setSession(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      SetNotification('Error loading count', 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 5000);
    return () => clearInterval(interval);
  }, [id]);

  if (loading) {
    return (
      <div className="live-count-page d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-pink mb-3" role="status"></div>
          <h4 style={{ color: 'var(--color-foreground)' }}>Loading live count...</h4>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="live-count-page d-flex align-items-center justify-content-center">
        <h3 style={{ color: 'var(--color-foreground)' }}>Session not found</h3>
      </div>
    );
  }

  const displayCount =
    session.final_count ?? session.actual_count ?? session.ai_max_count ?? 0;

  const countLabel =
    session.actual_count != null
      ? 'Actual Count'
      : session.ai_max_count != null
      ? 'AI Count'
      : 'Current Count';

  return (
    <div className="live-count-page">
      {/* Top bar */}
      <div className="live-top-bar d-flex justify-content-between align-items-center px-4 py-3">
        <div className="d-flex align-items-center gap-3">
          <img
            src="https://anbar-bcs.org/anbar-bcs.png"
            alt="Anbar BCS"
            className="live-logo"
          />
          <div>
            <div className="live-subtitle">Live People Counter</div>
            <h4 className="live-title mb-0">{session.title}</h4>
          </div>
        </div>

        <div className="text-end">
          <small className="live-updated d-block">
            {lastUpdated
              ? `Updated ${lastUpdated.toLocaleTimeString()}`
              : '—'}
          </small>
          <Link to={`/sessions/${id}`} className="btn btn-sm live-back-btn mt-1">
            ← Back to Session
          </Link>
        </div>
      </div>

      {/* Main big number */}
      <div className="live-main d-flex flex-column align-items-center justify-content-center">
        <div className="count-label">{countLabel}</div>

        <div className="count-number">{displayCount}</div>

        <div className="mt-4">
          {session.is_active ? (
            <span className="live-badge live">● LIVE SESSION</span>
          ) : (
            <span className="live-badge ended">Session Ended</span>
          )}
        </div>
      </div>

      {/* Bottom info cards */}
      <div className="live-bottom d-flex justify-content-center gap-4 pb-5 flex-wrap">
        <div className="info-card">
          <div className="info-label">AI Count</div>
          <div className="info-value">{session.ai_max_count ?? '—'}</div>
        </div>
        <div className="info-card">
          <div className="info-label">Actual Count</div>
          <div className="info-value">{session.actual_count ?? '—'}</div>
        </div>
        <div className="info-card highlight">
          <div className="info-label">Final Count</div>
          <div className="info-value">{session.final_count ?? '—'}</div>
        </div>
      </div>

      {/* Styles using your exact colors */}
      <style>{`
        :root {
          --page-color-background: #FFF8FB;
          --page-color-foreground: #3D1F2E;
          --page-color-primary-pink: #E85D87;
          --page-color-primary-dark: #5C2A40;
          --page-color-light-pink: #F6DCE7;
          --page-color-soft-blush: #FCECF3;
          --page-color-accent-purple: #8C5B72;
          --page-color-white: #FFFFFF;
          --color-background: #FFF8FB;
          --color-foreground: #3D1F2E;
          --color-teal: #5C2A40;
          --color-teal-deep: #3D1F2E;
          --color-teal-soft: #E85D87;
          --color-teal-mist: #FCECF3;
          --color-gold: #D4AF7A;
          --color-gold-bright: #F5E8C7;
          --color-gold-soft: #E8D5B0;
          --color-ink: #3D1F2E;
          --color-muted: #6F5460;
          --color-ribbon: #8C5B72;
          --color-deep: #3D1F2E;
          --color-deep-soft: #4A1F32;
          --color-deep-mid: #5C2A40;
        }

        .live-count-page {
          min-height: 100vh;
          background: var(--page-color-background);
          color: var(--color-foreground);
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          overflow-x: hidden;
        }

        .live-top-bar {
          background: var(--page-color-white);
          border-bottom: 1px solid var(--page-color-light-pink);
          box-shadow: 0 2px 12px rgba(61, 31, 46, 0.06);
        }

        .live-logo {
          height: 52px;
          width: auto;
          object-fit: contain;
        }

        .live-subtitle {
          font-size: 0.85rem;
          color: var(--color-muted);
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .live-title {
          color: var(--page-color-primary-dark);
          font-weight: 700;
        }

        .live-updated {
          color: var(--color-muted);
          font-size: 0.8rem;
        }

        .live-back-btn {
          border: 1px solid var(--page-color-primary-pink);
          color: var(--page-color-primary-pink);
          background: transparent;
          border-radius: 8px;
          padding: 0.25rem 0.75rem;
          font-size: 0.85rem;
          transition: all 0.2s ease;
        }
        .live-back-btn:hover {
          background: var(--page-color-primary-pink);
          color: white;
        }

        .live-main {
          flex: 1;
          min-height: 58vh;
          text-align: center;
          padding: 2rem 1rem;
        }

        .count-label {
          font-size: 1.15rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--page-color-accent-purple);
          font-weight: 600;
          margin-bottom: 0.75rem;
        }

        .count-number {
          font-size: clamp(6.5rem, 20vw, 13rem);
          font-weight: 800;
          line-height: 1;
          color: var(--page-color-primary-dark);
          text-shadow: 0 8px 30px rgba(232, 93, 135, 0.18);
          animation: softPulse 3.5s ease-in-out infinite;
        }

        @keyframes softPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.025); }
        }

        .live-badge {
          display: inline-block;
          padding: 0.45rem 1.4rem;
          border-radius: 50px;
          font-weight: 600;
          font-size: 0.95rem;
          letter-spacing: 0.04em;
        }
        .live-badge.live {
          background: var(--page-color-primary-pink);
          color: white;
          box-shadow: 0 4px 15px rgba(232, 93, 135, 0.35);
        }
        .live-badge.ended {
          background: var(--page-color-light-pink);
          color: var(--page-color-primary-dark);
        }

        .live-bottom {
          padding: 0 1rem 2.5rem;
        }

        .info-card {
          background: var(--page-color-white);
          border: 1px solid var(--page-color-light-pink);
          border-radius: 18px;
          padding: 1.35rem 2.2rem;
          min-width: 170px;
          text-align: center;
          box-shadow: 0 4px 18px rgba(61, 31, 46, 0.06);
          transition: transform 0.2s ease;
        }
        .info-card:hover {
          transform: translateY(-3px);
        }

        .info-card.highlight {
          background: linear-gradient(135deg, var(--page-color-soft-blush), #fff);
          border-color: var(--page-color-primary-pink);
          box-shadow: 0 6px 22px rgba(232, 93, 135, 0.15);
        }

        .info-label {
          font-size: 0.82rem;
          color: var(--color-muted);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin-bottom: 0.4rem;
          font-weight: 500;
        }

        .info-value {
          font-size: 1.9rem;
          font-weight: 700;
          color: var(--page-color-primary-dark);
        }

        .info-card.highlight .info-value {
          color: var(--page-color-primary-pink);
        }

        .spinner-border.text-pink {
          color: var(--page-color-primary-pink) !important;
          width: 2.8rem;
          height: 2.8rem;
        }

        @media (max-width: 640px) {
          .live-logo {
            height: 42px;
          }
          .live-bottom {
            flex-direction: column;
            align-items: center;
          }
          .info-card {
            width: 85%;
            max-width: 280px;
          }
        }
      `}</style>
    </div>
  );
};

export default LiveCountDisplay;